import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

declare global {
  interface Window {
    daum: any;
    PortOne: any;
  }
}

// 포트원 설정 (config.py에서 관리, 프론트는 storeId/channelKey만 필요)
const PORTONE_STORE_ID = 'store-b41127b4-d01b-4e42-8d2c-dffb4d3eb714';
const PORTONE_CHANNEL_KEY_TOSS = 'channel-key-581b0252-51a4-404a-b999-3636782695a2';
const PORTONE_CHANNEL_KEY_NAVERPAY = '';  // 네이버페이 채널 추가 후 입력

interface OrderItem {
  product_id: number;
  product_name: string;
  main_image: string;
  price: number;
  quantity: number;
  option_name?: string;
  cart_id?: number;
}

type PayMethod = 'CARD' | 'TRANSFER' | 'TOSSPAY' | 'NAVERPAY';

const PAY_METHODS: { value: PayMethod; label: string; icon: string }[] = [
  { value: 'CARD', label: '신용/체크카드', icon: '💳' },
  { value: 'TRANSFER', label: '계좌이체', icon: '🏦' },
  { value: 'TOSSPAY', label: '토스페이', icon: '🔵' },
  { value: 'NAVERPAY', label: '네이버페이', icon: '🟢' },
];

const OrderPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { member, token, updateProfile, loading: authLoading } = useAuth();
  const { removeItem, refresh } = useCart();

  const [orderItems, setOrderItems] = useState<OrderItem[]>(() => {
    // 새 탭에서 열린 경우 localStorage에서 주문 데이터 즉시 로드
    const pending = localStorage.getItem('pendingOrder');
    if (pending) {
      try {
        const parsed = JSON.parse(pending);
        localStorage.removeItem('pendingOrder');
        return parsed.items || [];
      } catch {}
    }
    return [];
  });
  const [payMethod, setPayMethod] = useState<PayMethod>('CARD');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    postal_code: '',
    address: '',
    detail_address: '',
    pccc: '',
    memo: '',
    email: ''
  });
  const [shippingFee, setShippingFee] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const detailAddressRef = useRef<HTMLInputElement>(null);

  const openPostcode = () => {
    if (!window.daum?.Postcode) {
      alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    new window.daum.Postcode({
      oncomplete: (data: any) => {
        const address = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
        setForm(prev => ({
          ...prev,
          postal_code: data.zonecode,
          address: address
        }));
        detailAddressRef.current?.focus();
      }
    }).open();
  };

  useEffect(() => {
    const state = location.state as { items?: OrderItem[]; shippingFee?: number } | null;
    if (state?.items && state.items.length > 0) {
      setOrderItems(state.items);
      if (typeof state.shippingFee === 'number') setShippingFee(state.shippingFee);
    } else if (orderItems.length === 0) {
      navigate('/');
    }
  }, [location.state, navigate, orderItems.length]);

  useEffect(() => {
    if (member) {
      setForm(prev => ({
        ...prev,
        name: member.name || '',
        phone: member.phone || '',
        postal_code: member.postal_code || '',
        address: member.address || '',
        detail_address: member.detail_address || '',
        pccc: member.pccc || '',
        email: member.email || ''
      }));
    }
  }, [member]);

  const formatPrice = (p: number) => p.toLocaleString() + '원';

  const productTotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalPrice = productTotal + shippingFee;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const getChannelKey = (method: PayMethod) => {
    if (method === 'NAVERPAY') return PORTONE_CHANNEL_KEY_NAVERPAY;
    return PORTONE_CHANNEL_KEY_TOSS; // CARD, TRANSFER, TOSSPAY → 토스페이먼츠
  };

  const getPortOnePayMethod = (method: PayMethod) => {
    switch (method) {
      case 'CARD': return 'CARD';
      case 'TRANSFER': return 'TRANSFER';
      case 'TOSSPAY': return 'EASY_PAY';
      case 'NAVERPAY': return 'EASY_PAY';
      default: return 'CARD';
    }
  };

  const handleSubmit = async () => {
    if (!member) {
      setError('회원가입 후 주문할 수 있습니다. 로그인해 주세요');
      return;
    }
    if (!form.name || !form.phone || !form.address || !form.postal_code) {
      setError('이름, 연락처, 우편번호, 주소를 모두 입력해주세요');
      return;
    }
    if (!form.pccc) {
      setError('개인통관부호를 입력해주세요 (해외배송 필수)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 1. 회원이면 프로필에 배송정보 저장
      if (member && token) {
        await updateProfile({
          phone: form.phone,
          address: form.address,
          detail_address: form.detail_address,
          postal_code: form.postal_code,
          pccc: form.pccc
        } as any);
      }

      // 2. 주문 사전 생성 (회원/비회원 공통)
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const prepareRes = await fetch('/api/storefront/orders/prepare', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: orderItems.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            option_name: item.option_name || null,
            quantity: item.quantity,
            price: item.price
          })),
          shipping: {
            name: form.name,
            phone: form.phone,
            postal_code: form.postal_code,
            address: form.address,
            detail_address: form.detail_address,
            pccc: form.pccc,
            memo: form.memo
          },
          total_price: totalPrice,
          pay_method: payMethod,
          guest_email: !member ? form.email : '',
          guest_phone: !member ? form.phone : ''
        })
      });

      if (!prepareRes.ok) {
        const err = await prepareRes.json();
        setError(err.detail || '주문 생성에 실패했습니다');
        setLoading(false);
        return;
      }

      const { order_number, order_name } = await prepareRes.json();

      // 3. 포트원 결제 호출
      if (PORTONE_STORE_ID && window.PortOne) {
        const paymentResponse = await window.PortOne.requestPayment({
          storeId: PORTONE_STORE_ID,
          channelKey: getChannelKey(payMethod),
          paymentId: order_number,
          orderName: order_name,
          totalAmount: totalPrice,
          currency: 'KRW',
          payMethod: getPortOnePayMethod(payMethod),
          customer: {
            fullName: form.name,
            phoneNumber: form.phone.replace(/-/g, ''),
            email: member?.email || form.email || undefined
          },
          customData: { member_id: member?.id || null }
        });

        if (paymentResponse.code) {
          // 결제 실패/취소
          setError(paymentResponse.message || '결제가 취소되었습니다');
          setLoading(false);
          return;
        }

        // 4. 결제 검증
        const confirmHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) confirmHeaders['Authorization'] = `Bearer ${token}`;

        const confirmRes = await fetch('/api/storefront/orders/confirm', {
          method: 'POST',
          headers: confirmHeaders,
          body: JSON.stringify({
            payment_id: order_number,
            tx_id: paymentResponse.txId
          })
        });

        if (!confirmRes.ok) {
          const err = await confirmRes.json();
          setError(err.detail || '결제 확인에 실패했습니다');
          setLoading(false);
          return;
        }
      } else {
        // 포트원 미설정 → 바로 confirm (테스트 모드)
        const confirmHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) confirmHeaders['Authorization'] = `Bearer ${token}`;

        const confirmRes = await fetch('/api/storefront/orders/confirm', {
          method: 'POST',
          headers: confirmHeaders,
          body: JSON.stringify({
            payment_id: order_number
          })
        });

        if (!confirmRes.ok) {
          const err = await confirmRes.json();
          setError(err.detail || '주문 확인에 실패했습니다');
          setLoading(false);
          return;
        }
      }

      // 5. 장바구니 정리 + 완료 페이지 이동
      for (const item of orderItems) {
        if (item.cart_id) await removeItem(item.cart_id);
      }
      await refresh();

      // 비회원이면 주문번호를 localStorage에 저장 (주문조회용)
      if (!member) {
        const guestOrders = JSON.parse(localStorage.getItem('ws_guest_orders') || '[]');
        guestOrders.push({ order_number, phone: form.phone });
        // 메모리 보호: 최대 50개만 유지 (오래된 것 삭제)
        while (guestOrders.length > 50) guestOrders.shift();
        localStorage.setItem('ws_guest_orders', JSON.stringify(guestOrders));
      }

      navigate('/order/complete', { state: { orderId: order_number, totalPrice, isGuest: !member } });

    } catch (err: any) {
      setError(err?.message || '서버 연결에 실패했습니다');
    }
    setLoading(false);
  };

  // 회원만 주문할 수 있다 - 로그인하지 않았으면 주문서 대신 안내를 보여 준다
  const goAuth = (path: string) => {
    try {
      sessionStorage.setItem('ws_after_login_order', JSON.stringify({ items: orderItems, shippingFee }));
    } catch {}
    navigate(path);
  };

  if (!authLoading && !member) {
    return (
      <>
        <Header />
        <div className="order-page">
          <div className="order-inner">
            <h2 className="order-title">주문서</h2>
            <div className="order-guest-notice" style={{ textAlign: 'center', padding: '36px 20px' }}>
              <div style={{ fontSize: 16, marginBottom: 6 }}><span>회원 전용</span></div>
              <div>주문은 회원가입 후 이용하실 수 있습니다.</div>
              <div style={{ marginTop: 20 }}>
                <button type="button" onClick={() => goAuth('/login')}
                  style={{ padding: '10px 28px', marginRight: 10, border: '1px solid #333', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
                  로그인
                </button>
                <button type="button" onClick={() => goAuth('/register')}
                  style={{ padding: '10px 28px', border: '1px solid #333', background: '#333', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
                  회원가입
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="order-page">
        <div className="order-inner">
          <h2 className="order-title">주문서</h2>

          {error && <div className="order-error">{error}</div>}

          <div className="order-layout">
            {/* 왼쪽: 배송정보 + 결제수단 + 주문상품 */}
            <div className="order-left">
              {/* 배송 정보 */}
              <div className="order-section">
                <h3 className="order-section-title">배송 정보</h3>
                <div className="order-field">
                  <label>수령인 *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="수령인 이름" />
                </div>
                <div className="order-field">
                  <label>연락처 *</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="010-0000-0000" />
                </div>
                {!member && (
                  <div className="order-field">
                    <label>이메일</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="주문 확인용 (선택)" />
                  </div>
                )}
                <div className="order-field">
                  <label>주소 *</label>
                  <div className="order-address-row">
                    <input type="text" name="postal_code" value={form.postal_code} readOnly placeholder="우편번호" className="order-postal-input" />
                    <button type="button" className="order-address-btn" onClick={openPostcode}>주소 검색</button>
                  </div>
                  <input type="text" name="address" value={form.address} readOnly placeholder="주소 검색 버튼을 클릭하세요" style={{ marginTop: 8 }} />
                </div>
                <div className="order-field">
                  <label>상세주소</label>
                  <input type="text" name="detail_address" value={form.detail_address} onChange={handleChange} placeholder="아파트, 동/호수 등" ref={detailAddressRef} />
                </div>
                <div className="order-field">
                  <label>개인통관부호 *</label>
                  <input type="text" name="pccc" value={form.pccc} onChange={handleChange} placeholder="P로 시작하는 13자리 (예: P123456789012)" />
                  <span className="order-field-hint">해외 직구 상품 통관에 필요합니다. <a href="https://unipass.customs.go.kr/csp/index.do" target="_blank" rel="noopener noreferrer">통관부호 발급</a></span>
                </div>
                <div className="order-field">
                  <label>배송 메모</label>
                  <select name="memo" value={form.memo} onChange={handleChange}>
                    <option value="">배송 메모를 선택해주세요</option>
                    <option value="부재시 문 앞에 놓아주세요">부재시 문 앞에 놓아주세요</option>
                    <option value="부재시 경비실에 맡겨주세요">부재시 경비실에 맡겨주세요</option>
                    <option value="배송 전 연락 바랍니다">배송 전 연락 바랍니다</option>
                    <option value="직접입력">직접입력</option>
                  </select>
                  {form.memo === '직접입력' && (
                    <input
                      type="text"
                      name="memo"
                      value=""
                      onChange={handleChange}
                      placeholder="배송 메모를 입력해주세요"
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              </div>

              {/* 결제 수단 */}
              <div className="order-section">
                <h3 className="order-section-title">결제 수단</h3>
                <div className="pay-methods">
                  {PAY_METHODS.map(m => (
                    <button
                      key={m.value}
                      type="button"
                      className={`pay-method-btn ${payMethod === m.value ? 'active' : ''}`}
                      onClick={() => setPayMethod(m.value)}
                    >
                      <span className="pay-method-icon">{m.icon}</span>
                      <span className="pay-method-label">{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 주문 상품 */}
              <div className="order-section">
                <h3 className="order-section-title">주문 상품 ({orderItems.length})</h3>
                <div className="order-items">
                  {orderItems.map((item, idx) => (
                    <div key={idx} className="order-item">
                      <img src={item.main_image || 'https://via.placeholder.com/80x80?text=No+Image'} alt={item.product_name} className="order-item-img" />
                      <div className="order-item-info">
                        <div className="order-item-name">{item.product_name}</div>
                        {item.option_name && <div className="order-item-option">{item.option_name}</div>}
                        <div className="order-item-qty">수량: {item.quantity}</div>
                      </div>
                      <div className="order-item-price">{formatPrice(item.price * item.quantity)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 오른쪽: 결제 요약 */}
            <div className="order-right">
              <div className="order-summary">
                <h3>결제 정보</h3>
                <div className="order-summary-row">
                  <span>상품 금액</span>
                  <span>{formatPrice(productTotal)}</span>
                </div>
                <div className="order-summary-row">
                  <span>배송비</span>
                  <span style={{ color: shippingFee > 0 ? '#e62e04' : '#00b894', fontWeight: 600 }}>
                    {shippingFee > 0 ? formatPrice(shippingFee) : '무료'}
                  </span>
                </div>
                <div className="order-summary-divider" />
                <div className="order-summary-row order-summary-total">
                  <span>총 결제 금액</span>
                  <span>{formatPrice(totalPrice)}</span>
                </div>
                <div className="order-selected-pay">
                  {PAY_METHODS.find(m => m.value === payMethod)?.icon} {PAY_METHODS.find(m => m.value === payMethod)?.label}
                </div>
                <button
                  className="order-pay-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? '결제 처리 중...' : `${formatPrice(totalPrice)} 결제하기`}
                </button>
                <p className="order-agree-text">
                  주문 시 <Link to="/terms">이용약관</Link> 및 <Link to="/privacy">개인정보처리방침</Link>에 동의합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

// 주문 완료 페이지
export const OrderCompletePage: React.FC = () => {
  const location = useLocation();
  const state = location.state as { orderId?: string; totalPrice?: number; isGuest?: boolean } | null;

  return (
    <>
      <Header />
      <div className="order-page">
        <div className="order-complete">
          <div className="order-complete-icon">
            <svg viewBox="0 0 24 24" width="72" height="72" fill="#00b894">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h2>주문이 완료되었습니다!</h2>
          {state?.orderId && <p className="order-complete-id">주문번호: {state.orderId}</p>}
          {state?.totalPrice && <p className="order-complete-price">결제 금액: {state.totalPrice.toLocaleString()}원</p>}
          <p className="order-complete-desc">주문 확인 후 해외 발송이 진행됩니다.<br />배송까지 7~15일 소요됩니다.</p>
          <div className="order-complete-actions">
            {state?.isGuest ? (
              <Link to="/order/lookup" className="order-complete-btn primary">주문 조회하기</Link>
            ) : (
              <Link to="/mypage" className="order-complete-btn primary">주문 내역 보기</Link>
            )}
            <Link to="/" className="order-complete-btn">쇼핑 계속하기</Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderPage;
